import { useEffect, useState, useRef } from 'react';
import { Network } from '@capacitor/network';

/**
 * Hook to monitor network connectivity status using Capacitor Network API
 *
 * @returns boolean indicating if the device is connected to the internet
 */
export const useNetworkStatus = (): boolean => {
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const listenerHandleRef = useRef<{ remove: () => void } | null>(null);

  useEffect(() => {
    const setup = async () => {
      try {
        // Get initial network status
        const status = await Network.getStatus();
        setIsOnline(status.connected);

        // Listen for network status changes
        listenerHandleRef.current = await Network.addListener(
          'networkStatusChange',
          status => {
            setIsOnline(status.connected);
          },
        );
      } catch {
        // Fallback to online if we can't determine status
        setIsOnline(true);
      }
    };

    setup();

    // Cleanup listener on unmount
    return () => {
      if (listenerHandleRef.current) {
        listenerHandleRef.current.remove();
        listenerHandleRef.current = null;
      }
    };
  }, []);

  return isOnline;
};
